import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import {
  PROPERTY_TYPES, categoryMeta, orderedCategories,
  type HebergementProperty, type HebergementCategory,
} from '@/lib/hebergement';

interface HebergementSearchTagProps {
  limit?: number;
}

interface MediaRow { property_id: number | null; url: string; type: string; is_cover: boolean; sort_order: number }

/**
 * Balise {{hebergement_search}} — recherche d'hébergements avec filtres.
 * Mise en page compacte : filtres en petits carrés (4 par ligne) et
 * résultats en grille de 4 mini-cartes dans une zone défilante, pour
 * ne plus jamais prendre toute la hauteur de la page.
 */
export default function HebergementSearchTag({ limit }: HebergementSearchTagProps) {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  const [properties, setProperties] = useState<HebergementProperty[]>([]);
  const [categories, setCategories] = useState<HebergementCategory[]>([]);
  const [covers, setCovers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [guests, setGuests] = useState('');

  useEffect(() => {
    if (!isTenant || !merchantId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [propRes, mediaRes, catRes] = await Promise.all([
          supabase
            .from('hebergement_properties')
            .select('*')
            .eq('merchant_id', merchantId)
            .eq('published', true)
            .eq('status', 'active')
            .order('created_at', { ascending: false }),
          supabase
            .from('hebergement_media')
            .select('property_id, url, type, is_cover, sort_order')
            .eq('merchant_id', merchantId)
            .order('sort_order', { ascending: true }),
          supabase.from('typehebergement').select('*').order('sort_order', { ascending: true }),
        ]);
        if (cancelled) return;
        setProperties(((propRes.data || []) as HebergementProperty[]));
        const map: Record<number, string> = {};
        for (const m of (mediaRes.data || []) as MediaRow[]) {
          if (m.property_id == null || m.type === 'video') continue;
          if (!map[m.property_id] || m.is_cover) map[m.property_id] = m.url;
        }
        setCovers(map);
        setCategories(
          ((catRes.data || []) as HebergementCategory[]).filter(
            (c) => c.idcommerce == null || Number(c.idcommerce) === Number(merchantId),
          ),
        );
      } catch {
        /* bloc silencieux : on n'affiche pas d'erreur technique sur le site public */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId]);

  const typeOptions = useMemo(() => {
    if (categories.length > 0) {
      return orderedCategories(categories).map(({ cat }) => ({
        value: cat.slug,
        label: cat.nom,
        icon: cat.icon || 'ri-building-4-line',
      }));
    }
    return PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label, icon: t.icon }));
  }, [categories]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const g = parseInt(guests, 10) || 0;
    const list = properties.filter((p) => {
      if (type && p.type !== type) return false;
      if (g > 0 && (p.capacity || 0) < g) return false;
      if (q) {
        const hay = [p.name, p.city, p.region, p.country, p.description].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return limit && limit > 0 ? list.slice(0, limit) : list;
  }, [properties, query, type, guests, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (properties.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-8 md:py-10">
      <div className="max-w-5xl mx-auto">
        <div className="mb-5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium mb-2">
            <i className="ri-search-eye-line"></i>Recherche
          </span>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Trouvez votre hébergement</h2>
          <p className="text-xs md:text-sm text-foreground-500 mt-1">
            Hôtels, riads, villas, appartements — disponibilités en temps réel.
          </p>
        </div>

        {/* Barre de recherche compacte */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <div className="lg:col-span-2 min-w-0">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ville, nom, riad…"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
            <div className="min-w-0">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Tous les types</option>
                {typeOptions.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <input
                type="number"
                min={1}
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                placeholder="Voyageurs"
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
          </div>

          {/* Filtres en petits carrés — 4 par ligne */}
          {typeOptions.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-1.5 mt-3">
              <button
                type="button"
                onClick={() => setType('')}
                className={`flex flex-col items-center justify-center gap-0.5 aspect-square rounded-lg border text-[9px] font-semibold leading-tight px-1 transition-colors cursor-pointer ${
                  type === ''
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'
                }`}
              >
                <i className="ri-apps-2-line text-sm"></i>
                <span className="truncate w-full text-center">Tous</span>
              </button>
              {typeOptions.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`flex flex-col items-center justify-center gap-0.5 aspect-square rounded-lg border text-[9px] font-semibold leading-tight px-1 transition-colors cursor-pointer ${
                    type === t.value
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'
                  }`}
                >
                  <i className={`${t.icon} text-sm`}></i>
                  <span className="truncate w-full text-center">{t.label}</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-2.5 gap-3 flex-wrap">
            <span className="text-[11px] text-foreground-500">{visible.length} hébergement(s)</span>
            <Link to="/hebergements" className="text-[11px] font-medium text-primary-600 hover:text-primary-700 no-underline whitespace-nowrap flex items-center gap-1">
              Voir tout <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
        </div>

        {/* Résultats : mini-cartes carrées dans une zone défilante */}
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-2.5">
              <i className="ri-hotel-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-800">Aucun hébergement ne correspond</p>
            <p className="text-xs text-foreground-500 mt-1">Essayez un autre type ou une autre ville.</p>
          </div>
        ) : (
          <div className="mt-4 max-h-[420px] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
              {visible.map((p) => {
                const meta = categoryMeta(categories, p.type);
                const cover = covers[p.id] || p.cover_image || '';
                const price = Number(p.price) || 0;
                return (
                  <Link
                    key={p.id}
                    to={`/hebergement/${p.id}`}
                    className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300/80 transition-colors no-underline"
                  >
                    <div className="relative aspect-square bg-background-100 overflow-hidden">
                      {cover ? (
                        <img
                          src={cover}
                          alt={p.name}
                          title={`${p.name} — ${meta.label}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-foreground-300">
                          <i className="ri-image-line text-2xl"></i>
                        </div>
                      )}
                      <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-background-50/95 text-primary-700 whitespace-nowrap">
                        {price.toLocaleString()} {currency}
                      </span>
                      <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-background-50/90 text-foreground-600 flex items-center justify-center">
                        <i className={`${meta.icon} text-[10px]`}></i>
                      </span>
                    </div>
                    <div className="p-2">
                      <p className="text-[11px] font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
                        {p.name}
                      </p>
                      <p className="text-[10px] text-foreground-500 truncate mt-0.5">
                        <i className="ri-map-pin-line mr-0.5"></i>
                        {p.city || p.country || meta.label}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}